import React, { type MouseEventHandler, useCallback, useState, useContext } from 'react';
import { IconButton } from '@atlaskit/button/new';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { token } from '@atlaskit/tokens';
import { isFavoritableItemTypename } from '@atlassian/jira-favourite-change-provider/src/model/utils.tsx';
import { useChangeFavorite } from '@atlassian/jira-favourite-change-provider/src/view/FavoriteChangeConsumer.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	STAR,
	UNSTAR,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/star.tsx';
import { useStopPropagation } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import { AnalyticContext } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/common/controllers/analytics-context/index.tsx';
import type { StarredOLFItemAnalytics } from '../../../types.tsx';
import { messages } from './messages.tsx';

export type StarJiraItemButtonProps = {
	analytics: StarredOLFItemAnalytics;
	defaultIsStarred?: boolean;
	id: string;
	name?: string;
	typename: string;
};

export function StarJiraItemButton(params: StarJiraItemButtonProps): React.JSX.Element {
	const {
		analytics: { itemId, itemType },
		defaultIsStarred = false,
		id,
		name,
		typename,
	} = params;
	const { formatMessage } = useIntl();

	if (!isFavoritableItemTypename(typename)) {
		throw new Error(`Encountered unexpected item type: ${typename}`);
	}

	const [isStarred, setIsStarred] = useState(defaultIsStarred);

	const { changeFavoriteMutation } = useChangeFavorite();

	const { fireUIAnalyticForStarrButton } = useContext(AnalyticContext);

	const handleOnClick = useCallback<MouseEventHandler>(() => {
		setIsStarred((prevIsStarred) => {
			const shouldStarJiraItem = !prevIsStarred;
			changeFavoriteMutation({ id, typename, value: shouldStarJiraItem });

			fireUIAnalyticForStarrButton({
				starType: shouldStarJiraItem ? STAR : UNSTAR,
				parentItemId: itemId,
				parentItemType: itemType,
			});

			return shouldStarJiraItem;
		});
	}, [changeFavoriteMutation, id, fireUIAnalyticForStarrButton, itemId, itemType, typename]);

	const handleOnClickWithStopPropagation = useStopPropagation(handleOnClick);

	const unstarLabel = formatMessage(messages.unstarButtonLabelWithName, { name: name || '' });
	const starLabel = formatMessage(messages.starButtonLabelWithName, { name: name || '' });

	return isStarred ? (
		<IconButton
			appearance="subtle"
			icon={(iconProps) => (
				<StarStarredIcon {...iconProps} color={token('color.icon.accent.yellow')} />
			)}
			onClick={handleOnClickWithStopPropagation}
			label={unstarLabel}
		/>
	) : (
		<IconButton
			appearance="subtle"
			icon={StarUnstarredIcon}
			onClick={handleOnClickWithStopPropagation}
			label={starLabel}
		/>
	);
}
